import { useMutation } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';

import { RemindJobApplicationDocument } from '@xing-com/crate-jobs-graphql';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';

type SendJobApplicationReminderParams = {
  email: string;
  postingId: string;
  locale: string;
  postingUrl: string;
  trackingContext: string;
};

type UseJobApplicationReminderReturnValue = {
  sendJobApplicationReminder: (
    mutationParams: SendJobApplicationReminderParams
  ) => void;
  isReminderInProgress: boolean;
};

const MIN_MUTATION_TIMEOUT_MS = 2000;

// TODO: move this hook to @xing-com/crate-jobs-domain-apply-discard-reminder-modal-logged-out once it's not imported from instant apply lo xinglet
export const useJobApplicationReminder = (
  onReminderSent: () => void
): UseJobApplicationReminderReturnValue => {
  const [isMinTimeoutPending, setIsMinTimeoutPending] = useState(false);
  const trackNwtEvent = useJobsNwtTracking();

  const [
    remindJobApplicationMutation,
    { loading: isMutationLoading, called: isMutationCalled },
  ] = useMutation(RemindJobApplicationDocument);

  const sendJobApplicationReminder = useCallback(
    ({
      email,
      postingId,
      locale,
      postingUrl,
      trackingContext,
    }: SendJobApplicationReminderParams) => {
      setIsMinTimeoutPending(true);
      setTimeout(() => {
        setIsMinTimeoutPending(false);
      }, MIN_MUTATION_TIMEOUT_MS);

      const itemUrn = `surn:x-xing:jobs:posting:${postingId}`;

      remindJobApplicationMutation({
        variables: {
          input: { email, locale, postingId, postingUrl },
        },
        onCompleted: (): void => {
          trackNwtEvent({
            eventSchema: 'basic',
            event: 'activated',
            element: 'apply_reminder',
            elementDetail: 'job_application_cancel_modal',
            itemUrn,
            page: 'jobs/apply',
            context: trackingContext,
          });
        },
      });
    },
    [remindJobApplicationMutation, trackNwtEvent]
  );

  useEffect(() => {
    if (isMutationCalled && !isMutationLoading && !isMinTimeoutPending) {
      onReminderSent();
    }
  }, [
    isMinTimeoutPending,
    isMutationCalled,
    isMutationLoading,
    onReminderSent,
  ]);

  return {
    sendJobApplicationReminder,
    isReminderInProgress: isMutationCalled,
  };
};
